import React, { useEffect, useState } from "react";
import PageGuide from "../../components/PageGuide";
import { Helmet } from "react-helmet-async";
import ApiCall from "../../utils/apicall";
import { useSelector } from "react-redux";
import { hasPermission, PERMISSIONS } from "../../utils/hasPermission";
import {
  Container, Card, Stack, Typography, Button, TextField, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Dialog,
  DialogTitle, DialogContent, DialogActions, IconButton, InputAdornment, Box, Tooltip,
  Grid, MenuItem,
} from "@mui/material";
import Iconify from "../../components/iconify";
import { StatusPill, KpiCard } from "../../components/WeldAssure";
import { printWQTR } from "../../utils/weldAssurePrint";
import { BrandedDialogHeader, SectionHeader, FIELD_SX } from "../../components/WeldAssure/BrandedForm";

import SmartTable from "../../components/SmartTable/SmartTable";
const EMPTY_FORM = {
  wqtrNumber: "", revision: "", wqtrDate: "", welderName: "", welderStamp: "",
  authorizedBy: "", process: "", position: "",
  transferMode: "", electrodes: "", current: "", backing: "", insert: "",
  materialFrom: "", materialTo: "", plateDimensions: "", pipeDimensions: "",
  fillerMetal: "", shieldingGas: "",
  thicknessQualified: "", diameterQualified: "", positionQualified: "", progression: "",
  visual: "", bendResult: "", result: "", testResults: "", notes: "",
};

function buildDocumentData(form) {
  return {
    process: form.process,
    variables: {
      transferMode: form.transferMode, electrodes: form.electrodes, current: form.current,
      position: form.position, backing: form.backing, insert: form.insert,
      materialFrom: form.materialFrom, materialTo: form.materialTo,
      plateDimensions: form.plateDimensions, pipeDimensions: form.pipeDimensions,
      fillerMetal: form.fillerMetal, shieldingGas: form.shieldingGas,
    },
    ranges: { thicknessQualified: form.thicknessQualified, diameterQualified: form.diameterQualified, positionQualified: form.positionQualified, progression: form.progression },
    results: { visual: form.visual, bendResult: form.bendResult, result: form.result },
    testResults: form.testResults, notes: form.notes,
  };
}

function flattenDocumentData(doc) {
  const dd = doc.documentData || {};
  const v = dd.variables || {}, r = dd.ranges || {}, rs = dd.results || {};
  const g = (x) => x || "";
  return {
    wqtrNumber: g(doc.wqtrNumber), revision: g(doc.revision), wqtrDate: g(doc.wqtrDate),
    welderName: g(doc.welderName), welderStamp: g(doc.welderStamp), authorizedBy: g(doc.authorizedBy),
    process: g(dd.process || doc.process), position: g(v.position),
    transferMode: g(v.transferMode), electrodes: g(v.electrodes), current: g(v.current),
    backing: g(v.backing), insert: g(v.insert), materialFrom: g(v.materialFrom), materialTo: g(v.materialTo),
    plateDimensions: g(v.plateDimensions), pipeDimensions: g(v.pipeDimensions),
    fillerMetal: g(v.fillerMetal), shieldingGas: g(v.shieldingGas),
    thicknessQualified: g(r.thicknessQualified), diameterQualified: g(r.diameterQualified),
    positionQualified: g(r.positionQualified), progression: g(r.progression),
    visual: g(rs.visual), bendResult: g(rs.bendResult), result: g(rs.result),
    testResults: g(dd.testResults), notes: g(dd.notes),
  };
}

const YN = ["", "Yes", "No"];
const CUR = ["", "AC", "DCEP", "DCEN", "Pulsed"];
const PROG = ["", "Up", "Down"];
const PASSFAIL = ["", "Pass", "Fail", "N/A"];
const ACCEPT = ["", "Acceptable", "Rejected", "N/A"];
const RESULT = ["", "Pass", "Fail"];

export default function WQTRDocuments() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // S-4 approval: Admin and Quality Manager only; the server enforces the same rule.
  const [approveTarget, setApproveTarget] = useState(null);
  const [approveError, setApproveError] = useState("");
  const [approving, setApproving] = useState(false);
  const { loginUser } = useSelector((state) => state.userSlice);
  const userPermissions = loginUser?.decodedToken?.permissions ?? [];
  const canApprove = hasPermission(userPermissions, PERMISSIONS.APPROVE_WELD_PROCEDURE);

  const fetchDocs = async () => {
    try { setLoading(true); const res = await ApiCall.get("/wqtr", { params: { search } }); setDocs(res.data?.data || res.data || []); }
    catch (err) { console.error("fetchWQTR:", err); } finally { setLoading(false); }
  };
  useEffect(() => { fetchDocs(); /* eslint-disable-next-line */ }, [search]);

  const handleOpen = async (doc = null) => {
    if (doc) { const res = await ApiCall.get(`/wqtr/${doc.id}`); const full = res.data?.data || res.data; setEditing(full); setFormData(flattenDocumentData(full)); }
    else { setEditing(null); setFormData(EMPTY_FORM); }
    setOpenForm(true);
  };
  const handleClose = () => { setOpenForm(false); setEditing(null); setFormData(EMPTY_FORM); };
  const f = (field) => (e) => setFormData((p) => ({ ...p, [field]: e.target.value }));

  const handleSave = async () => {
    if (!formData.wqtrNumber.trim()) return;
    setSubmitLoading(true);
    try {
      const payload = {
        wqtrNumber: formData.wqtrNumber, revision: formData.revision, wqtrDate: formData.wqtrDate || null,
        welderName: formData.welderName, welderStamp: formData.welderStamp, authorizedBy: formData.authorizedBy,
        process: formData.process, documentData: buildDocumentData(formData),
      };
      if (editing) await ApiCall.put(`/wqtr/${editing.id}`, payload);
      else await ApiCall.post("/wqtr", payload);
      handleClose(); fetchDocs();
    } catch (err) { console.error("saveWQTR:", err); } finally { setSubmitLoading(false); }
  };
  const handlePrint = async (d) => {
    try { const res = await ApiCall.get(`/wqtr/${d.id}`); printWQTR(res.data?.data || res.data); }
    catch (err) { console.error("printWQTR:", err); printWQTR(d); }
  };
  const handleApprove = async () => {
    if (!approveTarget) return;
    setApproving(true); setApproveError("");
    try { await ApiCall.post(`/wqtr/${approveTarget.id}/approve`, {}); setApproveTarget(null); fetchDocs(); }
    catch (err) { setApproveError(err?.response?.data?.message || "Approval failed. Try again."); }
    finally { setApproving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try { await ApiCall.delete(`/wqtr/${deleteTarget.id}`); setDeleteTarget(null); fetchDocs(); }
    catch (err) { console.error("deleteWQTR:", err); }
  };

  const Fld = ({ label, field, sm = 3, select, options = [], ...rest }) => (
    <Grid item xs={12} sm={sm}>
      <TextField label={label} value={formData[field] ?? ""} onChange={f(field)} fullWidth size="small" select={select || undefined} sx={FIELD_SX} {...rest}>
        {select && options.map((o) => <MenuItem key={o} value={o}>{o || "—"}</MenuItem>)}
      </TextField>
    </Grid>
  );
  const Sec = ({ children }) => (<Grid item xs={12}><SectionHeader>{children}</SectionHeader></Grid>);

  const welders = new Set(docs.map(d => d.welderName).filter(Boolean));

  return (
    <>
      <Helmet><title>WQTR Documents | WeldAssure</title></Helmet>
      <Container maxWidth="lg">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography variant="h4">WQTR Documents</Typography>
          <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={() => handleOpen()}>New WQTR</Button>
        </Stack>
        <PageGuide
          why="Prove each welder is qualified for the welds they make."
          who="The QM, proving each welder's qualification."
          what="Your Welder Qualification Test Records (WQTR), each printable as a controlled document."
          how="Record the welder's qualification test, then Print PDF to produce the controlled WQTR."
          when="Before a welder works to a procedure — and as qualifications come up for renewal."
          standard="AWS D1.1 §4.18"
        />

        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}><KpiCard label="Active WQTRs" value={docs.length} sub="qualification records" subKind="success" /></Grid>
          <Grid item xs={12} sm={6} md={3}><KpiCard label="Welders Qualified" value={welders.size} sub="distinct welders" subKind="info" /></Grid>
          <Grid item xs={12} sm={6} md={3}><KpiCard label="Passed" value={docs.filter(d => d.documentData?.results?.result === "Pass").length} sub="result: pass" subKind="warning" /></Grid>
          <Grid item xs={12} sm={6} md={3}><KpiCard label="AWS Code" value="D1.1:2020" sub="all conformed" subKind="neutral" /></Grid>
        </Grid>

        <Card sx={{ borderColor: "#E8ECF0", border: "1px solid" }}>
          <Box sx={{ p: 2 }}>
            <TextField size="small" placeholder="Search WQTR number, welder, process…" value={search} onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: (<InputAdornment position="start"><Iconify icon="eva:search-fill" /></InputAdornment>) }} sx={{ width: 340 }} />
          </Box>
          <TableContainer component={Paper} elevation={0}>
            <SmartTable tableId="wqtr-documents" lockedColumns={["WQTR Number", "Rev", "Welder", "Status"]}>
              <TableHead sx={{ "& .MuiTableCell-head": { backgroundColor: "#1C2636", color: "#FFFFFF", fontWeight: 700, fontSize: 11, letterSpacing: 0.3, textTransform: "uppercase", py: 1.5 } }}>
                <TableRow>
                  <TableCell>WQTR Number</TableCell><TableCell>Rev</TableCell><TableCell>Test Date</TableCell>
                  <TableCell>Welder</TableCell><TableCell>Process</TableCell><TableCell>Position</TableCell>
                  <TableCell>Status</TableCell><TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={{ "& .MuiTableRow-root:nth-of-type(even)": { backgroundColor: "#F7F9FC" }, "& .MuiTableCell-body": { borderBottomColor: "#E8ECF0", py: 1.5 } }}>
                {loading ? (<TableRow><TableCell colSpan={8} align="center">Loading…</TableCell></TableRow>)
                : docs.length === 0 ? (<TableRow><TableCell colSpan={8} align="center">No WQTR documents found</TableCell></TableRow>)
                : docs.map((d) => {
                    const isSuperseded = d.approvalStatus === "superseded";
                    const isDraft = d.approvalStatus === "pending" || !d.approvalStatus;
                    const statusLabel = isSuperseded ? "Superseded" : isDraft ? "Draft" : "Approved";
                    const statusKind = isSuperseded ? "neutral" : isDraft ? "warning" : "success";
                    return (
                      <TableRow key={d.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={700} sx={{ color: "#141A24" }}>{d.wqtrNumber}</Typography>
                          <Typography variant="caption" sx={{ color: "#959EA8", fontSize: 10 }}>AWS D1.1 Clause 4</Typography>
                        </TableCell>
                        <TableCell><Typography variant="body2" fontWeight={600}>{d.revision || "—"}</Typography></TableCell>
                        <TableCell><Typography variant="body2" sx={{ color: "#555F6B" }}>{d.wqtrDate || "—"}</Typography></TableCell>
                        <TableCell><Typography variant="body2" fontWeight={500}>{d.welderName || "—"}</Typography></TableCell>
                        <TableCell>{d.process || d.documentData?.process || "—"}</TableCell>
                        <TableCell>{d.documentData?.variables?.position || "—"}</TableCell>
                        <TableCell><Tooltip title={d.approvedAt ? `Approved by ${d.approvedByName || "user " + d.approvedBy} on ${String(d.approvedAt).slice(0, 10)}` : ""}><span><StatusPill label={statusLabel} kind={statusKind} /></span></Tooltip></TableCell>
                        <TableCell align="right">
                          <Tooltip title="Print PDF"><IconButton size="small" onClick={() => handlePrint(d)}><Iconify icon="eva:printer-fill" /></IconButton></Tooltip>
                          {isDraft && canApprove && (
                            <Tooltip title="Approve"><IconButton size="small" color="success" onClick={() => { setApproveError(""); setApproveTarget(d); }}><Iconify icon="eva:checkmark-circle-2-fill" /></IconButton></Tooltip>
                          )}
                          {isDraft ? (
                          <>
                          <Tooltip title="Edit"><IconButton size="small" onClick={() => handleOpen(d)}><Iconify icon="eva:edit-fill" /></IconButton></Tooltip>
                          <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleteTarget(d)}><Iconify icon="eva:trash-2-fill" /></IconButton></Tooltip>
                          </>
                          ) : (
                            <Tooltip title="Approved records are locked. Create a new revision to change it."><IconButton size="small" disabled><Iconify icon="eva:lock-fill" /></IconButton></Tooltip>
                          )}
                        </TableCell>
                      </TableRow>);
                  })}
              </TableBody>
            </SmartTable>
          </TableContainer>
        </Card>
      </Container>

      <Dialog open={openForm} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
        <BrandedDialogHeader title={editing ? `Edit WQTR — ${editing.wqtrNumber}` : "New WQTR Document"} subtitle="Welder Qualification Test Record · AWS D1.1 Clause 4" code="AWS D1.1:2020" onClose={handleClose} />
        <DialogContent sx={{ pt: 2.5 }}>
          <Grid container spacing={1.5}>
            <Sec>Header</Sec>
            <Fld label="WQTR Number *" field="wqtrNumber" sm={4} />
            <Fld label="Revision" field="revision" sm={2} />
            <Fld label="Test Date" field="wqtrDate" sm={3} type="date" InputLabelProps={{ shrink: true }} />
            <Fld label="Welder Name" field="welderName" sm={3} />
            <Fld label="Welder Stamp/ID" field="welderStamp" sm={4} />
            <Fld label="Authorized By" field="authorizedBy" sm={4} />
            <Fld label="Process(es)" field="process" sm={2} />
            <Fld label="Position" field="position" sm={2} placeholder="e.g. 3G" />

            <Sec>Qualified Variables</Sec>
            <Fld label="Transfer Mode" field="transferMode" />
            <Fld label="Electrodes" field="electrodes" />
            <Fld label="Current" field="current" select options={CUR} />
            <Fld label="Backing" field="backing" select options={YN} />
            <Fld label="Insert" field="insert" />

            <Sec>Material & Consumables</Sec>
            <Fld label="Material (From)" field="materialFrom" />
            <Fld label="Material (To)" field="materialTo" />
            <Fld label="Plate Dimensions" field="plateDimensions" />
            <Fld label="Pipe Dimensions" field="pipeDimensions" />
            <Fld label="Filler Metal" field="fillerMetal" />
            <Fld label="Shielding Gas" field="shieldingGas" />

            <Sec>Qualified Ranges</Sec>
            <Fld label="Thickness Qualified" field="thicknessQualified" />
            <Fld label="Diameter Qualified" field="diameterQualified" />
            <Fld label="Position Qualified" field="positionQualified" />
            <Fld label="Progression" field="progression" select options={PROG} />

            <Sec>Test Results</Sec>
            <Fld label="Visual" field="visual" select options={ACCEPT} />
            <Fld label="Bend / RT Result" field="bendResult" select options={PASSFAIL} />
            <Fld label="Overall Result" field="result" select options={RESULT} />
            <Grid item xs={12}><TextField label="Test Results (detail)" value={formData.testResults} onChange={f("testResults")} fullWidth multiline rows={2} size="small" sx={FIELD_SX} /></Grid>
            <Grid item xs={12}><TextField label="Notes" value={formData.notes} onChange={f("notes")} fullWidth multiline rows={2} size="small" sx={FIELD_SX} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ bgcolor: "#F7F9FC", borderTop: "1px solid #E8ECF0", px: 3, py: 1.75 }}>
          <Button onClick={handleClose} sx={{ color: "text.secondary" }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={submitLoading || !formData.wqtrNumber.trim()}>
            {submitLoading ? "Saving…" : editing ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!approveTarget} onClose={() => !approving && setApproveTarget(null)} maxWidth="xs">
        <DialogTitle>Approve WQTR {approveTarget?.wqtrNumber}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">Your name and today's date are recorded on this WQTR and in the audit log. Once approved, the record is locked; a change needs a new revision. The person who created the record cannot approve it.</Typography>
          {approveError && <Typography variant="body2" color="error" sx={{ mt: 1.5 }}>{approveError}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveTarget(null)} disabled={approving}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleApprove} disabled={approving}>{approving ? "Approving…" : "Approve"}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs">
        <DialogContent sx={{ pt: 3 }}><Typography>Permanently remove WQTR <strong>{deleteTarget?.wqtrNumber}</strong>?</Typography></DialogContent>
        <DialogActions><Button onClick={() => setDeleteTarget(null)}>Cancel</Button><Button variant="contained" color="error" onClick={handleDelete}>Delete</Button></DialogActions>
      </Dialog>
    </>
  );
}
